import { useCallback, useRef, type ReactNode } from 'react';
import type { CaseView } from '@chemoflow/contracts';
import { isStalled, minutesSince, selectors } from '@chemoflow/domain';
import { Icon, IconSwap } from '../components/Icon.js';
import {
  DoneArticle,
  DoneButton,
  ListSection,
  PatientWho,
  StatusPill,
  cls,
} from '../components/Card.js';
import { fmt } from '../lib/format.js';
import { useCardMotion } from '../lib/motion.js';
import { laterRecords, nurseStage } from '../lib/undo.js';
import { useBoardData } from '../state/board.js';
import { useClock } from '../state/clock.js';
import { useUi } from '../state/ui.js';

/** 調製前カード。数メートル離れた大型モニタからも状態が分かる作りを保つ。 */
function RxWaitCard({ case: c, now }: { case: CaseView; now: number }) {
  const { act, settings, isBusy } = useBoardData();
  const { confirmTap } = useUi();
  if (!c.clerk) return null;
  const { type, reason, at } = c.clerk;
  const alert = isStalled(c, now, settings.alertMin);
  // 送信中は止める。2回目のタップは版が古く、自分の操作が「他の端末で」として返る
  const busy = isBusy(c.caseId);

  const run = async (action: 'pharm_start' | 'stop_ack') => {
    const label = action === 'pharm_start' ? '調製開始' : '中止の確認';
    const ok = await confirmTap({
      action: label,
      name: c.patient?.name ?? '',
      patientId: c.patient?.patientId ?? c.surrogateKey,
      dept: c.patient?.dept ?? '',
      color: action === 'pharm_start' ? 'var(--start)' : 'var(--stop)',
    });
    if (!ok) return;
    void act({ case: c, action });
  };

  return (
    <article className={`card rx ${alert ? 'is-alert' : ''}`.trim()} data-id={c.caseId}>
      <div className="row">
        <StatusPill case={c} now={now} />
        <PatientWho case={c} rp />
        <p className={`rx-msg clamp2 ${type === 'stop' ? 'is-stop' : ''}`.trim()} title={reason}>
          {reason}
        </p>
        <div className="clock">
          <small className="clock-label">
            {type === 'stop' ? '中止連絡から' : 'お待ち時間'}{' '}
            <span className="alert-chip">
              <Icon name="warning" />
              未着手
            </span>
          </small>
          <b>
            {minutesSince(at, now)}
            <em>分</em>
          </b>
          <small>
            {type === 'change' ? '変更' : type === 'stop' ? '中止' : '確定'} {fmt(at)}
          </small>
        </div>
        {type === 'stop' ? (
          <button
            className={cls('btn start ack act', busy && 'is-off')}
            type="button"
            onClick={() => void run('stop_ack')}
            disabled={busy}
          >
            <IconSwap name="check" />
            確認
          </button>
        ) : (
          <button
            className={cls('btn start act', busy && 'is-off')}
            type="button"
            onClick={() => void run('pharm_start')}
            disabled={busy}
          >
            <IconSwap name="play_arrow" />
            調製開始
          </button>
        )}
      </div>
    </article>
  );
}

/**
 * クラークがまだ入力していない患者。調製前の下に別欄で並べ、この後に来る件数を見通せるようにする。
 * 押せるものがないので、ボタンは置かず点線の枠に「外来の入力待ち」と書く。
 */
function RxIdleCard({ case: c, now }: { case: CaseView; now: number }) {
  return (
    <article className="card rx-idle" data-id={c.caseId}>
      <div className="row">
        <StatusPill case={c} now={now} />
        <PatientWho case={c} rp />
        <span className="idle-note">
          <Icon name="schedule" />
          外来の入力待ち
        </span>
      </div>
    </article>
  );
}

/** 調製開始・調製完了のカードに出す、下流（化学療法室）の進み具合。 */
function downstream(c: CaseView, now: number, waiting: ReactNode) {
  if (c.adminDone) {
    return {
      pillCls: 'done',
      pill: (
        <>
          <Icon name="task_alt" />
          投与完了
        </>
      ),
      middle: (
        <span className="since is-done">
          投与完了 <b>{fmt(c.adminDone.at)}</b>
        </span>
      ),
    };
  }
  if (c.admin) {
    return {
      pillCls: 'admin',
      pill: (
        <>
          <Icon name="water_drop" />
          投与中
        </>
      ),
      middle: (
        <span className="since">
          投与から <b>{minutesSince(c.admin.at, now)}</b>分
        </span>
      ),
    };
  }
  return { pillCls: null, pill: null, middle: waiting };
}

/**
 * 調製開始カード。薬剤部での調製が終わったら「調製完了」を押して、下の調製完了の欄へ送る。
 * 投与が先に始まっていても完了は入れられる（ピルは下流の状態＝投与開始・投与完了を出す）。
 */
function RxStartedCard({ case: c, now, onUndo }: { case: CaseView; now: number; onUndo(): void }) {
  const { act, isBusy } = useBoardData();
  const { confirmTap } = useUi();
  if (!c.pharm) return null;
  const busy = isBusy(c.caseId);
  const down = downstream(
    c,
    now,
    <span className="since is-start">
      調製から <b>{minutesSince(c.pharm.at, now)}</b>分
    </span>,
  );

  const complete = async () => {
    const ok = await confirmTap({
      action: '調製完了',
      name: c.patient?.name ?? '',
      patientId: c.patient?.patientId ?? c.surrogateKey,
      dept: c.patient?.dept ?? '',
      color: 'var(--start)',
    });
    if (!ok) return;
    void act({ case: c, action: 'pharm_done' });
  };

  return (
    <DoneArticle
      case={c}
      tone="start"
      pillCls={down.pillCls ?? 'start'}
      pill={
        down.pill ?? (
          <>
            <Icon name="play_arrow" />
            調製開始済み
          </>
        )
      }
      label="開始時間"
      ts={c.pharm.at}
      rp
      onUndo={onUndo}
      middle={down.middle}
      action={
        <DoneButton
          label="調製完了"
          icon="done_all"
          action="pharm_done"
          onClick={() => void complete()}
          disabled={busy}
        />
      }
    />
  );
}

/** 調製完了カード。薬剤部の手を離れた案件。ピルは下流の状態があればそれを出す。 */
function RxDoneCard({ case: c, now, onUndo }: { case: CaseView; now: number; onUndo(): void }) {
  if (!c.pharmDone) return null;
  // ピルは化学療法室の状況（投与前／投与中／投与完了）。調製完了は欄そのものが示すので重ねて書かない
  const down = downstream(
    c,
    now,
    <span className="since is-wait">
      完了から <b>{minutesSince(c.pharmDone.at, now)}</b>分
    </span>,
  );
  return (
    <DoneArticle
      case={c}
      tone="made"
      pillCls={down.pillCls ?? 'made'}
      pill={
        down.pill ?? (
          <>
            <Icon name="schedule" />
            投与前
          </>
        )
      }
      label="調製完了"
      ts={c.pharmDone.at}
      rp
      onUndo={onUndo}
      middle={down.middle}
    />
  );
}

/** 薬剤部（調製室）：次に、どれを作るか。 */
export function RxView() {
  const { cases, undo } = useBoardData();
  const { now } = useClock();
  const { ask } = useUi();
  const viewRef = useRef<HTMLElement>(null);
  useCardMotion(viewRef, cases);

  // 入力前（まだ外来の入力がない患者）は、調製前の件数に含めず下の別欄に出す
  const waitAll = selectors.rxWait(cases);
  const wait = waitAll.filter((c) => c.clerk);
  const idle = waitAll.filter((c) => !c.clerk);
  const started = selectors.rxStarted(cases);
  const made = selectors.rxDone(cases);
  const stopped = selectors.rxStop(cases);

  const undoStarted = useCallback(
    async (c: CaseView) => {
      if (c.admin) {
        const ok = await ask({
          title: `${c.patient?.name ?? 'この患者'} さんは化学療法室で${nurseStage(c)}済みです`,
          text: `戻すと${laterRecords(c, 'admin')}の記録も取り消されます。`,
          ok: '戻す',
          tone: 'danger',
        });
        if (!ok) return;
      }
      void undo(c);
    },
    [ask, undo],
  );

  /** 調製完了の取消。投与が進んでいる患者は確認を挟む（消えるのは調製完了だけ）。 */
  const undoMade = useCallback(
    async (c: CaseView) => {
      if (c.admin) {
        const ok = await ask({
          title: `${c.patient?.name ?? 'この患者'} さんは化学療法室で${nurseStage(c)}済みです`,
          text: '調製完了の記録だけを取り消し、［調製開始］に戻します。\n投与の記録はそのまま残ります。',
          ok: '戻す',
        });
        if (!ok) return;
      }
      void undo(c);
    },
    [ask, undo],
  );

  return (
    <main className="board is-rx view" id="view-rx" data-view="rx" ref={viewRef}>
      <section>
        <ListSection title="調製前" count={wait.length} emptyText="クラークの入力を待っています">
          {wait.map((c) => (
            <RxWaitCard key={c.caseId} case={c} now={now} />
          ))}
        </ListSection>
        <ListSection
          title="入力前"
          count={idle.length}
          headClass="sub"
          note="外来の入力が済むと、上の［調製前］に移ります"
          hideWhenEmpty
        >
          {idle.map((c) => (
            <RxIdleCard key={c.caseId} case={c} now={now} />
          ))}
        </ListSection>
      </section>
      <section>
        <ListSection
          title="調製開始"
          count={started.length}
          emptyText="まだ調製開始はありません"
          headClass="is-start"
        >
          {started.map((c) => (
            <RxStartedCard key={c.caseId} case={c} now={now} onUndo={() => void undoStarted(c)} />
          ))}
        </ListSection>
        <ListSection
          title="調製完了"
          count={made.length}
          emptyText="まだ調製完了はありません"
          headClass="is-made"
          icon="done_all"
        >
          {made.map((c) => (
            <RxDoneCard key={c.caseId} case={c} now={now} onUndo={() => void undoMade(c)} />
          ))}
        </ListSection>
        <ListSection title="中止（確認済）" count={stopped.length} headClass="sub" hideWhenEmpty>
          {stopped.map((c) =>
            c.stopAck ? (
              <DoneArticle
                key={c.caseId}
                case={c}
                tone="stop"
                pill={
                  <>
                    <Icon name="close" />
                    中止
                  </>
                }
                label="確認時間"
                ts={c.stopAck.at}
                extra="is-stopped"
                onUndo={() => void undo(c)}
              />
            ) : null,
          )}
        </ListSection>
      </section>
    </main>
  );
}
